import { useState, useEffect, useRef, useMemo } from "react";
import { OwcTypography, OwcIconButton, OwcInput } from "@one/react";
import PropTypes from "prop-types";
import InlineTypography from "./InlineTypography";
import { valueOrEmpty, formatUserDisplay } from "../../../../utils/helpers/text";
import { useSelector } from "react-redux";
import { withApollo } from "react-apollo";
import { LIST_DIGITAL_LAB_BOOKING_USERS } from "../../../../gql/bookingapi/queries";
import { QUICK_EDIT_BOOKING } from "../../../../gql/bookingapi/mutations";
import AssistantSelect from "../../AssistantSelect/AssistantSelect";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { quickEditTracker } from "../../../../features/booking-operations/quickEditTracker";

const formatAssistanceDuration = (v) => {
  if (v === null || v === undefined) return "-";
  if (typeof v === "number") return `${v}h`;
  const num = Number(v);
  if (!isNaN(num)) return `${num}h`;
  return String(v);
};

const calculateBookingDurationHours = (booking) => {
  const startRaw = booking?.dateFrom || booking?.start;
  const endRaw = booking?.dateTo || booking?.end;
  if (!startRaw || !endRaw) return null;

  const start = new Date(startRaw);
  const end = new Date(endRaw);
  const diffMs = end.getTime() - start.getTime();
  if (!Number.isFinite(diffMs) || diffMs <= 0) return null;

  return Math.max(0.5, Math.round((diffMs / 3600000) * 2) / 2);
};

const hasAssignedAssistant = (booking) => Boolean(booking?.assistent?.[0]);

// A new assistant can only be assigned when the booking requires assistance. Approval status and
// reviewer permissions do not bypass this. An already-assigned assistant stays editable so it can
// still be removed/changed if requiresAssistance was later switched off (booking edit keeps it).
const shouldShowEditIcon = ({ allowEdit, assistanceDeclaration, booking }) => {
  if (!allowEdit) return false;
  if (assistanceDeclaration?.requiresAssistance) return true;
  return hasAssignedAssistant(booking);
};

const resolveSavedApprovalStatus = ({ autoApproved, autoPending, currentApprovalStatus }) => {
  if (autoApproved) return "APPROVED";
  if (autoPending) return "PENDING";
  return currentApprovalStatus;
};

const getBookingId = (booking) => booking?.bookingId || booking?.id;

const resolveAssistantFromUsers = (assistantId, users) => {
  if (!assistantId || !users.length) return null;
  // Prefer the unique userId match; only fall back to name/username (which can collide
  // across users) when no userId match is found.
  const byUserId = users.find((candidate) => candidate.userId === assistantId);
  if (byUserId) return byUserId;
  return users.find((candidate) => (
    candidate.name === assistantId ||
    candidate.username === assistantId
  )) || null;
};

function isValidAssistantName(assistantName) {
  return Boolean(assistantName && assistantName !== "Not yet assigned");
}

const buildEditDraftState = ({ booking, users, assistanceDuration, assistantName }) => {
  const duration = assistanceDuration !== null && assistanceDuration !== undefined
    ? assistanceDuration.toString()
    : "";

  const bookingAssistantId = booking?.assistent?.[0];
  if (!bookingAssistantId) {
    return {
      duration,
      selectedAssistant: "",
      selectedAssistantName: "",
      originalAssistant: "",
      originalAssistantName: "",
    };
  }

  const matchedAssistant = resolveAssistantFromUsers(bookingAssistantId, users);
  if (matchedAssistant) {
    const formattedName = formatUserDisplay(matchedAssistant);
    return {
      duration,
      selectedAssistant: matchedAssistant.userId,
      selectedAssistantName: formattedName,
      originalAssistant: matchedAssistant.userId,
      originalAssistantName: formattedName,
    };
  }

  return {
    duration,
    selectedAssistant: bookingAssistantId,
    selectedAssistantName: isValidAssistantName(assistantName) ? assistantName : bookingAssistantId,
    originalAssistant: bookingAssistantId,
    originalAssistantName: isValidAssistantName(assistantName) ? assistantName : bookingAssistantId,
  };
};

const isValidHalfHourDuration = (value) => (
  Number.isFinite(value) &&
  value >= 0.5 &&
  Math.abs((value * 2) - Math.round(value * 2)) < 1e-9
);

const isDurationInputEmpty = (editedDuration) =>
  editedDuration === null || editedDuration === undefined || String(editedDuration).trim() === "";

const resolveAutoAssistanceDuration = (bookingDurationHours) => {
  if (Number(bookingDurationHours) > 0) {
    return { resolvedDuration: bookingDurationHours, validationMessage: "" };
  }
  return {
    resolvedDuration: null,
    validationMessage: "Duration is required because booking duration cannot be calculated",
  };
};

const resolveManualAssistanceDuration = ({ parsedDuration }) => {
  if (!isValidHalfHourDuration(parsedDuration)) {
    return { resolvedDuration: null, validationMessage: "Duration must be at least 0.5h and use 0.5h steps" };
  }
  return { resolvedDuration: parsedDuration, validationMessage: "" };
};

const resolveAssistanceDuration = ({ editedDuration, bookingDurationHours }) => {
  if (isDurationInputEmpty(editedDuration)) {
    return resolveAutoAssistanceDuration(bookingDurationHours);
  }
  return resolveManualAssistanceDuration({ parsedDuration: Number(editedDuration) });
};

const computeApprovalStatusChange = ({ approvalStatus, shouldClearAssistant }) => ({
  shouldAutoApprove: (approvalStatus === "PENDING" || approvalStatus === "REJECTED") && !shouldClearAssistant,
  shouldAutoPending: approvalStatus === "APPROVED" && shouldClearAssistant,
});

const buildSaveContext = ({ selectedAssistant, approvalStatus, editedDuration, booking }) => {
  const shouldClearAssistant = !selectedAssistant;
  const bookingDurationHours = calculateBookingDurationHours(booking);

  const { resolvedDuration, validationMessage } = shouldClearAssistant
    ? { resolvedDuration: null, validationMessage: "" }
    : resolveAssistanceDuration({ editedDuration, bookingDurationHours });

  const { shouldAutoApprove, shouldAutoPending } = computeApprovalStatusChange({ approvalStatus, shouldClearAssistant });

  return {
    shouldClearAssistant,
    resolvedAssistantDuration: resolvedDuration,
    shouldAutoApprove,
    shouldAutoPending,
    validationMessage,
    bookingDurationHours,
  };
};

const hasValidDurationForSave = (saveContext) => {
  if (saveContext.shouldClearAssistant) return true;
  if (saveContext.validationMessage) return false;
  return Boolean(saveContext.resolvedAssistantDuration && saveContext.resolvedAssistantDuration > 0);
};

const notifyEditModeChange = (onEditModeChange, value) => {
  if (onEditModeChange) {
    onEditModeChange(value);
  }
};

const setAssistanceCallbackRef = (assistanceUpdateCallbackRef, callback) => {
  if (assistanceUpdateCallbackRef) {
    assistanceUpdateCallbackRef.current = callback;
  }
};

const clearAssistanceCallbackRef = (assistanceUpdateCallbackRef) => {
  setAssistanceCallbackRef(assistanceUpdateCallbackRef, null);
};

const showAssistanceError = (message) => {
  snackbarService.show({
    type: "error",
    message,
    duration: 3000,
  });
};

const getAssistanceSaveValidationMessage = ({ resolvedBookingId, saveContext }) => {
  if (!resolvedBookingId) {
    return "Booking id is missing";
  }

  if (saveContext.validationMessage) {
    return saveContext.validationMessage;
  }

  if (!hasValidDurationForSave(saveContext)) {
    return "Please fill in all required fields";
  }

  return "";
};

const buildAssistanceQuickEditInput = ({ resolvedBookingId, saveContext, selectedAssistant, userEmail }) => ({
  bookingId: resolvedBookingId,
  assistent: saveContext.shouldClearAssistant ? [] : [selectedAssistant],
  assistentTime: saveContext.resolvedAssistantDuration,
  createdByEmail: userEmail,
});

const resolveSavedAssistantName = ({
  selectedAssistant,
  selectedAssistantName,
  originalAssistant,
  assistantName,
}) => {
  if (selectedAssistant === originalAssistant) {
    if (assistantName) return assistantName;
    if (selectedAssistantName) return selectedAssistantName;
    return selectedAssistant;
  }

  if (selectedAssistantName) return selectedAssistantName;
  return selectedAssistant;
};

const getSavedAssistantValues = ({
  shouldClearAssistant,
  selectedAssistant,
  selectedAssistantName,
  originalAssistant,
  assistantName,
}) => {
  if (shouldClearAssistant) {
    return {
      savedAssistantId: "",
      savedAssistantName: "",
    };
  }

  return {
    savedAssistantId: selectedAssistant,
    savedAssistantName: resolveSavedAssistantName({
      selectedAssistant,
      selectedAssistantName,
      originalAssistant,
      assistantName,
    }),
  };
};

const resolveSavedDurationInputValue = ({ shouldClearAssistant, resolvedAssistantDuration, _editedDuration }) => {
  if (shouldClearAssistant) return "";
  if (resolvedAssistantDuration === null || resolvedAssistantDuration === undefined) return "";
  return String(resolvedAssistantDuration);
};

const resolveApprovalStatusAfterSync = async ({
  requestedStatus,
  shouldSyncApprovalStatus,
  approvalStatus,
  savedValues,
  resolvedAssistantDuration,
  onApprovalStatusSyncRequested,
}) => {
  if (!shouldSyncApprovalStatus || !onApprovalStatusSyncRequested) {
    return shouldSyncApprovalStatus ? approvalStatus : requestedStatus;
  }

  const approvalSyncSucceeded = await onApprovalStatusSyncRequested({
    nextStatus: requestedStatus,
    assistantId: savedValues.savedAssistantId,
    assistantDuration: resolvedAssistantDuration,
  });

  return approvalSyncSucceeded ? requestedStatus : approvalStatus;
};

const applySavedAssistanceState = ({
  selectedAssistant,
  selectedAssistantName,
  savedValues,
  resolvedAssistantDuration,
  savedDurationInputValue,
  setOriginalAssistant,
  setOriginalAssistantName,
  setOriginalDuration,
  setEditedDuration,
  setSavedAssistantName,
  setSavedAssistanceDuration,
}) => {
  setOriginalAssistant(selectedAssistant);
  setOriginalAssistantName(selectedAssistantName);
  setOriginalDuration(savedDurationInputValue);
  setEditedDuration(savedDurationInputValue);
  setSavedAssistantName(savedValues.savedAssistantName || "");
  setSavedAssistanceDuration(resolvedAssistantDuration ?? null);
};

const notifySaveSuccess = ({
  onUpdateComplete,
  onAssistanceUpdateComplete,
  onAssistanceSaved,
  savedValues,
  resolvedAssistantDuration,
  resolvedStatus,
}) => {
  if (onUpdateComplete) {
    onUpdateComplete();
  }
  if (onAssistanceUpdateComplete) {
    onAssistanceUpdateComplete();
  }
  if (onAssistanceSaved) {
    onAssistanceSaved({
      assistantId: savedValues.savedAssistantId,
      assistantName: savedValues.savedAssistantName,
      assistantDuration: resolvedAssistantDuration,
      approvalStatus: resolvedStatus,
    });
  }
};

const completeQuickEditSave = async ({
  payload,
  saveContext,
  handleTrackerSuccess,
  handleTrackerError,
  finalizeSuccessfulSave,
  stopLoadingFlow,
}) => {
  if (payload?.commandId) {
    quickEditTracker.register(payload.commandId, 'UPDATE', {
      onSuccess: () => {
        handleTrackerSuccess(saveContext);
      },
      onError: handleTrackerError,
    });
    return;
  }

  await finalizeSuccessfulSave(saveContext);
  stopLoadingFlow();
};

const AssistanceDetailsHeader = ({
  showEditIcon,
  isEditMode,
  saving,
  hasChanges,
  canSave,
  onEdit,
  onCancel,
  onSave,
}) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: "4px", minHeight: "24px", position: "relative" }}>
    <OwcTypography style={{ width: "70%" }} variant="caption">Assistance details</OwcTypography>
    {showEditIcon && !isEditMode && (
      <div style={{ display: "flex", alignItems: "center", gap: "4px", paddingTop: "2px" }}>
        <OwcIconButton
          onClick={onEdit}
          style={{ color: "#0B41CD" }}
          icon="edit"
          type="outlined"
          flat={true}
        />
      </div>
    )}
    {showEditIcon && isEditMode && (
      <div style={{ display: "flex", alignItems: "center", gap: "8px", height: "40px" }}>
        <OwcIconButton
          onClick={onSave}
          style={{
            color: canSave ? "#28A745" : "#999999"
          }}
          icon="circle_confirm"
          type="filled"
          flat={true}
          disabled={saving || !canSave}
          data-testid="assistance-save-btn"
        />
        <OwcIconButton
          onClick={onCancel}
          style={{
            color: hasChanges ? "#DC3545" : "#999999"
          }}
          icon="circle_clear"
          type="filled"
          flat={true}
          disabled={saving}
          data-testid="assistance-cancel-btn"
        />
      </div>
    )}
  </div>
);

const AssistanceReadOnlyDetails = ({ assistantName, assistanceDuration, assistanceDeclaration }) => (
  <>
    <OwcTypography variant="caption">
      Assistant: <InlineTypography style={{ display: "inline-block" }} variant="button" element="span">
        {valueOrEmpty(assistantName, false, "Not yet assigned")}
      </InlineTypography>
    </OwcTypography>
    <OwcTypography variant="caption">
      Duration: <InlineTypography variant="button">
        {formatAssistanceDuration(assistanceDuration)}
      </InlineTypography>
    </OwcTypography>
    <OwcTypography variant="caption">
      Reason: <InlineTypography variant="button">
        {assistanceDeclaration?.assistanceReason || "-"}
      </InlineTypography>
    </OwcTypography>
  </>
);

const AssistanceEditableDetails = ({
  saving,
  selectedAssistant,
  selectedAssistantName,
  editedDuration,
  users,
  fetchingUsers,
  setSelectedAssistant,
  setSelectedAssistantName,
  setEditedDuration,
  assistanceDeclaration,
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", opacity: saving ? 0.6 : 1, transition: "opacity 0.2s" }}>
    <div>
      <AssistantSelect
        width="100%"
        selectedAssistantId={selectedAssistant}
        selectedAssistantName={selectedAssistantName}
        users={users}
        loading={fetchingUsers}
        style={{ maxWidth: "520px" }}
        onAssistantChange={(userId, displayName) => {
          setSelectedAssistant(userId);
          setSelectedAssistantName(displayName);
          if (!userId) {
            setEditedDuration("");
          }
        }}
        required={false}
        disabled={saving}
      />

    </div>

    <div>
      <OwcInput
        type="number"
        required={Boolean(selectedAssistant)}
        label="Duration"
        value={editedDuration}
        disabled={saving}
        min="0.5"
        step="0.5"
        placeholder="Leave empty for auto"
        onInputChange={(event) => {
          setEditedDuration(event.target?.value || "");
        }}
      >
        <div slot="suffix">h</div>
      </OwcInput>
    </div>

    <div>
      <OwcTypography variant="caption" style={{ color: "#544F4F" }}>
        Reason
      </OwcTypography>
      <OwcTypography variant="body2">
        {assistanceDeclaration?.assistanceReason || "-"}
      </OwcTypography>
    </div>
  </div>
);

const AssistanceDetails = ({
  show = true,
  assistanceDeclaration,
  assistanceDuration,
  assistantName,
  approvalStatus,
  client,
  booking,
  onUpdateComplete,
  onAssistanceUpdateStarted,
  onAssistanceUpdateComplete,
  assistanceUpdateCallbackRef,
  onClick,
  allowEdit = false,
  startLoading,
  stopLoading,
  setStatus,
  dispatchAction,
  onAssistanceSaved,
  onApprovalStatusSyncRequested,
  onEditModeChange,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [users, setUsers] = useState([]);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selectedAssistant, setSelectedAssistant] = useState("");
  const [selectedAssistantName, setSelectedAssistantName] = useState("");
  const [editedDuration, setEditedDuration] = useState("");
  const [savedAssistantName, setSavedAssistantName] = useState(assistantName || "");
  const [savedAssistanceDuration, setSavedAssistanceDuration] = useState(assistanceDuration ?? null);

  const [originalAssistant, setOriginalAssistant] = useState("");
  const [originalAssistantName, setOriginalAssistantName] = useState("");
  const [originalDuration, setOriginalDuration] = useState("");

  // Guards against the draft re-initializing (and wiping unsaved edits) when `users` reloads mid-edit.
  const editDraftInitializedRef = useRef(false);

  const reduxUsers = useSelector((state) => state.users.users);
  const user = useSelector((store) => store.user);

  useEffect(() => {
    if (!allowEdit && isEditMode) {
      setIsEditMode(false);
      notifyEditModeChange(onEditModeChange, false);
    }
  }, [allowEdit, isEditMode, onEditModeChange]);

  useEffect(() => {
    setSavedAssistantName(assistantName || "");
  }, [assistantName]);

  useEffect(() => {
    setSavedAssistanceDuration(assistanceDuration ?? null);
  }, [assistanceDuration]);

  useEffect(() => {
    return () => {
      clearAssistanceCallbackRef(assistanceUpdateCallbackRef);
    };
  }, [assistanceUpdateCallbackRef]);

  const showEditIcon = shouldShowEditIcon({ allowEdit, assistanceDeclaration, booking });

  // `booking` is a new object reference ~16x/sec (Mobiscroll re-render churn), so depend on
  // these derived primitives instead, otherwise fetchUsers refires constantly and stalls state updates.
  const bookingAssistantId = booking?.assistent?.[0] || null;
  const reservedForKey = useMemo(
    () => JSON.stringify([...(booking?.reservedForUsers || []), ...(booking?.reservedForEmails || [])]),
    [booking?.reservedForUsers, booking?.reservedForEmails]
  );

  useEffect(() => {
    // The person the booking is FOR shouldn't be assignable as their own assistant, but the
    // creator (e.g. an admin booking on someone else's behalf) must remain selectable.
    const filterOutReservedForUsers = (usersList) => {
      if (!booking) return usersList;
      const reservedForIds = [...(booking.reservedForUsers || []), ...(booking.reservedForEmails || [])];
      if (!reservedForIds.length) return usersList;
      const currentAssistantId = booking?.assistent?.[0];
      return usersList.filter((u) => {
        // Keep the currently-assigned assistant visible/selectable even if they're also a reservedFor user.
        if (currentAssistantId && (u.userId === currentAssistantId || u.email === currentAssistantId || u.name === currentAssistantId)) {
          return true;
        }
        return !reservedForIds.includes(u.userId) && !reservedForIds.includes(u.email) && !reservedForIds.includes(u.name);
      });
    };

    const fetchUsers = async () => {
      if (reduxUsers && reduxUsers.length > 0) {
        setUsers(filterOutReservedForUsers(reduxUsers));
        setFetchingUsers(false);
        return;
      }

      try {
        setFetchingUsers(true);
        const result = await client.query({
          query: LIST_DIGITAL_LAB_BOOKING_USERS,
          variables: { limit: 500 },
          fetchPolicy: "network-only",
        });

        if (result?.data?.listDigitalLabBookingUsers?.items) {
          setUsers(filterOutReservedForUsers(result.data.listDigitalLabBookingUsers.items));
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setFetchingUsers(false);
      }
    };

    if (showEditIcon || isEditMode) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditIcon, isEditMode, client, reduxUsers, bookingAssistantId, reservedForKey]);

  useEffect(() => {
    if (!isEditMode) {
      editDraftInitializedRef.current = false;
      return;
    }
    // Only seed the draft once per edit session; later `users` reloads must not clobber in-progress edits.
    if (editDraftInitializedRef.current || !booking) return;

    editDraftInitializedRef.current = true;
    const draft = buildEditDraftState({ booking, users, assistanceDuration, assistantName });
    setEditedDuration(draft.duration);
    setOriginalDuration(draft.duration);
    setSelectedAssistant(draft.selectedAssistant);
    setSelectedAssistantName(draft.selectedAssistantName);
    setOriginalAssistant(draft.originalAssistant);
    setOriginalAssistantName(draft.originalAssistantName);
  }, [isEditMode, booking, assistanceDuration, assistantName, users]);

  if (!show) {
    return <></>;
  }

  const hasChanges = selectedAssistant !== originalAssistant ||
    selectedAssistantName !== originalAssistantName ||
    editedDuration !== originalDuration;
  const canSave = hasChanges;

  const handleEdit = () => {
    if (!allowEdit) {
      if (onClick) onClick();
      return;
    }

    setIsEditMode(true);
    notifyEditModeChange(onEditModeChange, true);
  };


  const handleCancel = () => {
    setSelectedAssistant(originalAssistant);
    setSelectedAssistantName(originalAssistantName);
    setEditedDuration(originalDuration);
    setIsEditMode(false);
    notifyEditModeChange(onEditModeChange, false);
  };

  const updateStatusFlags = (value) => {
    if (dispatchAction) {
      dispatchAction({ type: "fetching", payload: value });
    }
    if (setStatus) {
      setStatus(value);
    }
  };

  const stopLoadingFlow = () => {
    updateStatusFlags(false);
    if (stopLoading) {
      stopLoading();
    }
  };

  const finalizeSuccessfulSave = async ({ shouldClearAssistant, resolvedAssistantDuration, shouldAutoApprove, shouldAutoPending }) => {
    const savedValues = getSavedAssistantValues({
      shouldClearAssistant,
      selectedAssistant,
      selectedAssistantName,
      originalAssistant,
      assistantName,
    });

    const requestedStatus = resolveSavedApprovalStatus({
      autoApproved: shouldAutoApprove,
      autoPending: shouldAutoPending,
      currentApprovalStatus: approvalStatus,
    });

    const shouldSyncApprovalStatus = shouldAutoApprove || shouldAutoPending;
    let resolvedStatus;

    resolvedStatus = await resolveApprovalStatusAfterSync({
      requestedStatus,
      shouldSyncApprovalStatus,
      approvalStatus,
      savedValues,
      resolvedAssistantDuration,
      onApprovalStatusSyncRequested,
    });

    const savedDurationInputValue = resolveSavedDurationInputValue({
      shouldClearAssistant,
      resolvedAssistantDuration,
      editedDuration,
    });

    applySavedAssistanceState({
      selectedAssistant,
      selectedAssistantName,
      savedValues,
      resolvedAssistantDuration,
      savedDurationInputValue,
      setOriginalAssistant,
      setOriginalAssistantName,
      setOriginalDuration,
      setEditedDuration,
      setSavedAssistantName,
      setSavedAssistanceDuration,
    });

    setIsEditMode(false);
    setSaving(false);
    notifyEditModeChange(onEditModeChange, false);

    notifySaveSuccess({
      onUpdateComplete,
      onAssistanceUpdateComplete,
      onAssistanceSaved,
      savedValues,
      resolvedAssistantDuration,
      resolvedStatus,
    });
  };

  const handleTrackerSuccess = async (saveContext) => {
    await finalizeSuccessfulSave(saveContext);
    stopLoadingFlow();
    clearAssistanceCallbackRef(assistanceUpdateCallbackRef);
  };

  const handleTrackerError = () => {
    stopLoadingFlow();
    setSaving(false);
    notifyEditModeChange(onEditModeChange, false);
    clearAssistanceCallbackRef(assistanceUpdateCallbackRef);
  };

  const handleSave = async () => {
    const resolvedBookingId = getBookingId(booking);

    const saveContext = buildSaveContext({
      selectedAssistant,
      approvalStatus,
      editedDuration,
      booking,
    });

    const validationMessage = getAssistanceSaveValidationMessage({
      resolvedBookingId,
      saveContext,
    });
    if (validationMessage) {
      showAssistanceError(validationMessage);
      return;
    }

    try {
      setSaving(true);

      updateStatusFlags(true);
      if (startLoading) {
        startLoading();
      }

      setAssistanceCallbackRef(assistanceUpdateCallbackRef, () => {
        finalizeSuccessfulSave(saveContext);
      });

      const input = buildAssistanceQuickEditInput({
        resolvedBookingId,
        saveContext,
        selectedAssistant,
        userEmail: user?.email,
      });

      const result = await client.mutate({
        mutation: QUICK_EDIT_BOOKING,
        fetchPolicy: 'no-cache',
        variables: { input },
      });

      const payload = result?.data?.quickEditBooking;
      if (!payload?.success) {
        throw new Error(payload?.message || 'Failed to update assistance details');
      }

      await completeQuickEditSave({
        payload,
        saveContext,
        handleTrackerSuccess,
        handleTrackerError,
        finalizeSuccessfulSave,
        stopLoadingFlow,
      });

      if (onAssistanceUpdateStarted) {
        onAssistanceUpdateStarted();
      }

    } catch (error) {
      console.error("Error updating assistance details:", error);
      showAssistanceError("Failed to update assistance details");
      setSaving(false);
      notifyEditModeChange(onEditModeChange, false);

      stopLoadingFlow();
      clearAssistanceCallbackRef(assistanceUpdateCallbackRef);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: 'column', width: '100%' }}>
      <AssistanceDetailsHeader
        showEditIcon={showEditIcon}
        isEditMode={isEditMode}
        saving={saving}
        hasChanges={hasChanges}
        canSave={canSave}
        onEdit={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleEdit();
        }}
        onCancel={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleCancel();
        }}
        onSave={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleSave();
        }}
      />

      {isEditMode ? (
        <AssistanceEditableDetails
          saving={saving}
          selectedAssistant={selectedAssistant}
          selectedAssistantName={selectedAssistantName}
          editedDuration={editedDuration}
          users={users}
          fetchingUsers={fetchingUsers}
          setSelectedAssistant={setSelectedAssistant}
          setSelectedAssistantName={setSelectedAssistantName}
          setEditedDuration={setEditedDuration}
          assistanceDeclaration={assistanceDeclaration}
        />
      ) : (
        <AssistanceReadOnlyDetails
          assistantName={savedAssistantName}
          assistanceDuration={savedAssistanceDuration}
          assistanceDeclaration={assistanceDeclaration}
        />
      )}
    </div>
  );
};

AssistanceDetails.propTypes = {
  assistanceDeclaration: PropTypes.shape({
    requiresAssistance: PropTypes.bool,
    assistanceReason: PropTypes.string
  }),
  assistanceDuration: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  assistantName: PropTypes.string,
  approvalStatus: PropTypes.string,
  client: PropTypes.object,
  booking: PropTypes.object,
  onUpdateComplete: PropTypes.func,
  onAssistanceUpdateStarted: PropTypes.func,
  onAssistanceUpdateComplete: PropTypes.func,
  assistanceUpdateCallbackRef: PropTypes.shape({
    current: PropTypes.func
  }),
  onClick: PropTypes.func,
  allowInlineEdit: PropTypes.bool,
  show: PropTypes.bool,
  startLoading: PropTypes.func,
  stopLoading: PropTypes.func,
  setStatus: PropTypes.func,
  dispatchAction: PropTypes.func,
  onAssistanceSaved: PropTypes.func,
  onApprovalStatusSyncRequested: PropTypes.func,
  onEditModeChange: PropTypes.func,
};

export default withApollo(AssistanceDetails);
